import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, Trash2, ImageIcon, Clock, Heart, MessageCircle, Star } from "lucide-react";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import { MentionInput } from "@/components/MentionInput";
import { renderWithMentions } from "@/lib/mentions";
import { getSection } from "@/lib/sections";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

const fmtDuration = (s) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const Broadcast = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [thumbBusy, setThumbBusy] = useState(false);
  const [showThumbEditor, setShowThumbEditor] = useState(false);
  const [pendingTime, setPendingTime] = useState(2);
  const [cloudinaryReady, setCloudinaryReady] = useState(false);

  // Companion post state (likes + comments live on the linked Post document,
  // so a comment posted here shows up on room feeds too).
  const [companionPost, setCompanionPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);

  const playerAspect = (() => {
    const ar = b?.asset_aspect_ratio;
    if (typeof ar === "string" && ar.includes(":")) {
      const [w, h] = ar.split(":").map((n) => parseFloat(n));
      if (w > 0 && h > 0) return `${w} / ${h}`;
    }
    return "16 / 9";
  })();
  const isPortrait = (() => {
    const ar = b?.asset_aspect_ratio;
    if (typeof ar === "string" && ar.includes(":")) {
      const [w, h] = ar.split(":").map((n) => parseFloat(n));
      return w > 0 && h > 0 && h > w;
    }
    return false;
  })();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/live/broadcasts/${id}`);
        setB(data);
        setPendingTime(data.thumbnail_time || 2);
        // Load the linked companion post so we can show likes + comments.
        const roomPostId = data?.visibility?.room_post_id;
        if (roomPostId) {
          try {
            const [p, c] = await Promise.all([
              api.get(`/posts/${roomPostId}`),
              api.get(`/posts/${roomPostId}/comments`),
            ]);
            setCompanionPost(p.data);
            setComments(c.data);
          } catch {
            // silent — page still works without discussion
          }
        }
      } catch {
        setB(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    api.get("/cloudinary/status").then(({ data }) => setCloudinaryReady(data?.ready)).catch(() => {});
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/live/broadcasts/${id}/download`);
      if (data.ready && data.url) {
        window.open(data.url, "_blank");
      } else {
        toast(data.message || "Recording is still being prepared. Try again in a minute.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not prepare download.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this broadcast permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/live/${id}`);
      toast.success("Broadcast deleted.");
      navigate("/live");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete.");
    }
  };

  const saveFrameThumbnail = async () => {
    setThumbBusy(true);
    try {
      const { data } = await api.post(`/live/${id}/thumbnail`, {
        thumbnail_time: pendingTime,
        clear_custom: true,
      });
      toast.success("Thumbnail updated.");
      setB({ ...b, thumbnail_time: data.thumbnail_time ?? pendingTime, custom_thumbnail_url: null });
      setShowThumbEditor(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update thumbnail.");
    } finally {
      setThumbBusy(false);
    }
  };

  const uploadCustomThumbnail = async (file) => {
    if (!file) return;
    if (!cloudinaryReady) {
      toast.error("Cloudinary not configured.");
      return;
    }
    setThumbBusy(true);
    try {
      const sig = await api.get("/cloudinary/signature", { params: { resource_type: "image", folder: "broadcasts/" } });
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.data.api_key);
      fd.append("timestamp", sig.data.timestamp);
      fd.append("signature", sig.data.signature);
      fd.append("folder", sig.data.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.data.cloud_name}/image/upload`, { method: "POST", body: fd });
      const u = await res.json();
      if (u.secure_url) {
        await api.post(`/live/${id}/thumbnail`, { custom_thumbnail_url: u.secure_url });
        toast.success("Thumbnail uploaded.");
        setB({ ...b, custom_thumbnail_url: u.secure_url });
        setShowThumbEditor(false);
      } else {
        toast.error("Upload failed.");
      }
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setThumbBusy(false);
    }
  };

  const toggleLike = async () => {
    if (!user) return toast.error("Please sign in to like.");
    if (!companionPost || likeBusy) return;
    setLikeBusy(true);
    try {
      const { data } = await api.post(`/posts/${companionPost.id}/like`);
      setCompanionPost({ ...companionPost, liked: data.liked, likes_count: data.likes_count });
    } finally {
      setLikeBusy(false);
    }
  };

  const [featureBusy, setFeatureBusy] = useState(false);
  const toggleFeature = async () => {
    if (!companionPost || featureBusy) return;
    setFeatureBusy(true);
    try {
      const { data } = await api.post(`/posts/${companionPost.id}/feature`, { editor_note: "" });
      setCompanionPost({ ...companionPost, featured: data.featured });
      toast.success(data.featured ? "Featured on the Editor's Journal." : "Removed from the Journal.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update.");
    } finally {
      setFeatureBusy(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please sign in to comment.");
    if (!companionPost) return;
    if (!commentText.trim()) return;
    setCommentBusy(true);
    try {
      const { data } = await api.post(`/posts/${companionPost.id}/comments`, { text: commentText.trim() });
      setComments([...comments, data]);
      setCommentText("");
    } finally {
      setCommentBusy(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success("Comment deleted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete comment.");
    }
  };

  if (loading) {
    return (
      <div className="app-shell">
        <Navbar />
        <div className="container-belle" style={{ paddingTop: 110 }}>Loading…</div>
      </div>
    );
  }

  if (!b) {
    return (
      <div className="app-shell">
        <Navbar />
        <div className="container-belle" style={{ paddingTop: 110, paddingBottom: 80 }}>
          <h1 className="editorial-h1" style={{ fontSize: 40 }}>Not found.</h1>
          <p>
            This broadcast may have been removed. <Link to="/live" className="link-underline" style={{ color: "var(--brand-accent)" }}>Back to /live</Link>
          </p>
        </div>
      </div>
    );
  }

  const isOwner = user?.is_editor && user?.id === b.editor_id;
  const duration = b.asset_duration_seconds || 0;
  const section = getSection(b.section);

  return (
    <div data-testid="broadcast-page" className="app-shell">
      <Navbar />
      <article className="container-belle" style={{ paddingTop: 110, paddingBottom: 80, maxWidth: 920 }}>
        <Link
          to={user ? "/live" : "/"}
          data-testid="broadcast-back-to-live"
          className="overline"
          style={{ color: "var(--brand-accent)" }}
        >
          ← {user ? "back to broadcasts" : "back to the salon"}
        </Link>

        <h1 className="editorial-h1" style={{ fontSize: "clamp(36px, 5vw, 56px)", marginTop: 14, marginBottom: 12 }}>
          {b.title || "Untitled broadcast"}
        </h1>
        <div className="overline" style={{ color: "var(--text-muted)", marginBottom: 14 }}>
          {section ? `${section.label} · ` : b.section ? `${b.section} · ` : ""}{fmtDate(b.ended_at)}
          {duration ? ` · ${fmtDuration(duration)}` : ""}
          {b.editor ? ` · with ${b.editor.display_name}` : ""}
        </div>
        {/* Room tag — deep-links back to the salon room this broadcast belongs to */}
        {section && (
          <div style={{ marginBottom: 24 }}>
            <Link
              to={`/room/${section.slug}`}
              data-testid="broadcast-room-tag"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
                color: section.accent, fontFamily: "'Outfit', sans-serif",
                fontWeight: 500, borderBottom: `1px solid ${section.accent}`,
                paddingBottom: 2,
              }}
            >
              → {section.label}
            </Link>
          </div>
        )}

        <div
          style={{
            aspectRatio: playerAspect,
            background: "#000",
            overflow: "hidden",
            marginBottom: 24,
            maxWidth: isPortrait ? 480 : "100%",
            margin: isPortrait ? "0 auto 24px auto" : "0 0 24px 0",
          }}
        >
          {b.asset_playback_id ? (
            <MuxPlayer
              playbackId={b.asset_playback_id}
              streamType="on-demand"
              style={{ width: "100%", height: "100%" }}
              data-testid="broadcast-player"
            />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#FAF9F5", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
              Mux is still finishing the recording. Refresh in a minute.
            </div>
          )}
        </div>

        {isOwner && b.asset_playback_id && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
            <button
              type="button"
              data-testid="download-broadcast"
              onClick={handleDownload}
              disabled={downloading}
              className="btn-secondary"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Download size={14} />
              {downloading ? "Preparing…" : "Download MP4"}
            </button>
            <button
              type="button"
              data-testid="edit-thumbnail"
              onClick={() => setShowThumbEditor(true)}
              className="btn-secondary"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <ImageIcon size={14} />
              Edit thumbnail
            </button>
            <button
              type="button"
              data-testid="delete-broadcast"
              onClick={handleDelete}
              className="btn-secondary"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--brand-accent)" }}
            >
              <Trash2 size={14} />
              Delete broadcast
            </button>
            {companionPost && (
              <button
                type="button"
                data-testid="feature-broadcast"
                onClick={toggleFeature}
                disabled={featureBusy}
                className="btn-secondary"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: companionPost.featured ? "var(--brand-accent)" : "transparent",
                  color: companionPost.featured ? "var(--text-inverse)" : "var(--brand-accent)",
                  borderColor: "var(--brand-accent)",
                }}
              >
                <Star size={14} strokeWidth={1.4} fill={companionPost.featured ? "var(--text-inverse)" : "none"} />
                {companionPost.featured ? "Featured in Letter" : "Feature in Letter"}
              </button>
            )}
          </div>
        )}

        {/* Likes + Comments — powered by the linked companion post so the
            conversation is shared with the room feed. */}
        {companionPost && (
          <>
            <div
              data-testid="broadcast-actions"
              style={{
                marginTop: 24, paddingTop: 24, paddingBottom: 24,
                borderTop: "1px solid var(--border-default)",
                borderBottom: "1px solid var(--border-default)",
                display: "flex", alignItems: "center", gap: 28,
              }}
            >
              <button
                onClick={toggleLike}
                disabled={!user || likeBusy}
                data-testid="broadcast-like-button"
                style={{
                  background: "transparent", border: "none",
                  cursor: user ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", gap: 8, padding: 0,
                  color: companionPost.liked ? "var(--brand-accent)" : "var(--text-secondary)",
                  fontSize: 14, letterSpacing: "0.1em",
                }}
              >
                <Heart size={20} strokeWidth={1.4} fill={companionPost.liked ? "var(--brand-accent)" : "none"} />
                {companionPost.likes_count} {companionPost.likes_count === 1 ? "like" : "likes"}
              </button>
              <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-secondary)", fontSize: 14 }}>
                <MessageCircle size={20} strokeWidth={1.4} /> {comments.length} {comments.length === 1 ? "comment" : "comments"}
              </span>
            </div>

            <section data-testid="broadcast-comments" style={{ marginTop: 40 }}>
              <div className="overline" style={{ marginBottom: 24 }}>The conversation</div>

              {user ? (
                <form onSubmit={submitComment} style={{ display: "flex", gap: 12, marginBottom: 36, alignItems: "flex-start" }}>
                  {user.avatar_url ? (
                    <img loading="lazy" decoding="async" src={user.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--bg-alt)" }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <MentionInput
                      value={commentText}
                      onChange={(v) => {
                        if (v.length > 2000) {
                          setCommentText(v.slice(0, 2000));
                          toast("This comment is at its limit — send it and continue in a new one.");
                        } else {
                          setCommentText(v);
                        }
                      }}
                      placeholder="Add a quiet thought on this broadcast… try @ to mention someone"
                      testId="broadcast-comment"
                      rows={2}
                      maxLength={2000}
                      style={{ minHeight: 60, resize: "vertical", borderBottom: "1px solid var(--border-dark)" }}
                    />
                    {commentText.length > 1800 && (
                      <div
                        data-testid="broadcast-comment-counter"
                        style={{
                          fontSize: 11,
                          color: commentText.length >= 2000 ? "var(--brand-accent)" : "var(--text-muted)",
                          fontFamily: "'Outfit', sans-serif",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          textAlign: "right",
                          marginTop: 4,
                        }}
                      >
                        {commentText.length >= 2000
                          ? "Comment is full"
                          : `${2000 - commentText.length} characters remaining`}
                      </div>
                    )}
                  </div>
                  <button type="submit" disabled={commentBusy} data-testid="broadcast-comment-submit" className="btn-primary">Post</button>
                </form>
              ) : (
                <p style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
                  <Link to="/login" className="link-underline" style={{ color: "var(--brand-accent)" }}>Sign in</Link> to join the conversation.
                </p>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                {comments.map((c) => (
                  <div key={c.id} data-testid={`broadcast-comment-${c.id}`} style={{ display: "flex", gap: 14 }}>
                    <Link to={`/profile/${c.author?.username}`}>
                      {c.author?.avatar_url ? (
                        <img loading="lazy" decoding="async" src={c.author.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--bg-alt)" }} />
                      )}
                    </Link>
                    <div style={{ flex: 1, paddingBottom: 16, borderBottom: "1px solid var(--border-default)" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                        <Link to={`/profile/${c.author?.username}`} className="font-serif" style={{ fontSize: 16, fontWeight: 500 }}>
                          {c.author?.display_name}
                        </Link>
                        <span className="overline">{fmtDate(c.created_at)}</span>
                        {user && (c.author?.id === user.id || user.is_editor) && (
                          <button
                            data-testid={`broadcast-delete-comment-${c.id}`}
                            onClick={() => deleteComment(c.id)}
                            title={c.author?.id === user.id ? "Delete your comment" : "Remove (editor)"}
                            style={{
                              marginLeft: "auto", background: "transparent", border: "none",
                              cursor: "pointer", color: "var(--text-muted)", padding: 0,
                              display: "flex", alignItems: "center",
                            }}
                          >
                            <Trash2 size={14} strokeWidth={1.4} />
                          </button>
                        )}
                      </div>
                      <p style={{ marginTop: 6, marginBottom: 0, fontSize: 15, lineHeight: 1.6, color: "var(--text-primary)" }}>{renderWithMentions(c.text)}</p>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p style={{ color: "var(--text-muted)", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", fontSize: 19 }}>
                    No comments yet — leave the first quiet word.
                  </p>
                )}
              </div>
            </section>
          </>
        )}
      </article>
      <Footer />

      {/* Thumbnail editor modal */}
      {showThumbEditor && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowThumbEditor(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(51,51,51,0.45)",
            backdropFilter: "blur(10px)", zIndex: 500,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <div style={{ background: "var(--surface)", border: "1px solid var(--border-default)", maxWidth: 640, width: "100%", padding: 32, boxShadow: "0 24px 64px rgba(51,51,51,0.18)" }}>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>Thumbnail</div>
            <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 6, marginBottom: 18 }}>
              Choose the cover.
            </h2>

            <div className="overline" style={{ marginTop: 14, marginBottom: 8 }}>Option A · Pick a frame</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <Clock size={14} color="var(--text-muted)" />
              <input
                type="range"
                min={0}
                max={Math.max(0, Math.floor(duration) - 1)}
                value={pendingTime}
                onChange={(e) => setPendingTime(parseInt(e.target.value, 10))}
                data-testid="thumb-time-slider"
                style={{ flex: 1, accentColor: "var(--brand-accent)" }}
              />
              <span className="overline" style={{ fontSize: 11, minWidth: 60, textAlign: "right" }}>
                {fmtDuration(pendingTime)}
              </span>
            </div>
            <div style={{ aspectRatio: "16 / 9", background: "#000", overflow: "hidden", marginBottom: 14 }}>
              <img
                loading="lazy" decoding="async" src={`https://image.mux.com/${b.asset_playback_id}/thumbnail.jpg?width=640&time=${pendingTime}`}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
            <button type="button" onClick={saveFrameThumbnail} disabled={thumbBusy} className="btn-primary" data-testid="save-frame-thumb" style={{ background: "var(--brand-accent)" }}>
              {thumbBusy ? "Saving…" : "Use this frame"}
            </button>

            <div style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid var(--border-default)" }}>
              <div className="overline" style={{ marginBottom: 8 }}>Option B · Upload your own</div>
              <label
                className="btn-secondary"
                style={{ cursor: thumbBusy ? "not-allowed" : "pointer", opacity: thumbBusy ? 0.5 : 1, display: "inline-block" }}
              >
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => uploadCustomThumbnail(e.target.files?.[0])}
                  disabled={thumbBusy}
                  data-testid="upload-custom-thumb"
                />
              </label>
              {b.custom_thumbnail_url && (
                <div style={{ marginTop: 14 }}>
                  <img loading="lazy" decoding="async" src={b.custom_thumbnail_url} alt="" style={{ maxWidth: 240, border: "1px solid var(--border-default)" }} />
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
              <button type="button" className="btn-secondary" onClick={() => setShowThumbEditor(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Broadcast;
